// Ein Punkt der Auswertung beim Abwaegen (seit 23.09.2026).
//
// Festlegung des Nutzers: Je Punkt stehen Empfehlung, Antwort an den Kunden
// und gegebenenfalls ein Gegenvorschlag vorn. Alles andere - Vertragsstelle,
// Begruendung, Wissensbasis - ist Hintergrund und liegt eingeklappt in einer
// ruhigen Zeile darunter. Die Einschaetzungen wirtschaftlich/Investoren sind
// dort nur farbige Punkte mit einem Wort, keine weiteren Marken: Die
// Empfehlung oben rechts ist die eine Farbe, auf die es ankommt.
//
// Ueberarbeitet am 23.09.2026 ("aufgeraeumtes, gefuehrtes Design"): vorher
// stand die Vertragsstelle als grosser Kasten VOR der Antwort, dazu vier
// Marken je Karte und die Ueberschrift in gesperrten Versalien.

import React, { useState } from 'react';
import { Check, MessageSquare, Undo2, AlertTriangle } from 'lucide-react';
import { T, karteStil, knopfStil, feldStil, markeStil } from '../../lib/designTokens';
import {
  EMPFEHLUNG_TEXT, KOMMENTAR_PLATZHALTER, STELLE_GEPRUEFT, STELLE_NACHSEHEN, STELLE_UNGEPRUEFT, ersatzAussage,
} from '../../lib/abwaegen';
import { Aufklapper, Belegliste, NummerKreis, textKnopfStil, urteilsFarbe } from './abwaegenAnzeige';

const EMPFEHLUNG = {
  zustimmen: { text: EMPFEHLUNG_TEXT.zustimmen, schrift: T.gut, rand: T.gutRand, grund: T.gutGrund },
  bedingung: { text: EMPFEHLUNG_TEXT.bedingung, schrift: T.warn, rand: T.warnRand, grund: T.warnGrund },
  ablehnen: { text: EMPFEHLUNG_TEXT.ablehnen, schrift: T.gefahr, rand: T.gefahrRand, grund: T.gefahrGrund },
};

function Kopfzeile({ punkt }) {
  const e = EMPFEHLUNG[punkt.empfehlung];
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
      <NummerKreis>{punkt.nummer}</NummerKreis>
      <div style={{ flex: 1, minWidth: 0, fontSize: T.tTitel, fontWeight: 700, color: T.navy, lineHeight: 1.4, paddingTop: 2 }}>
        {punkt.kurz || 'Ohne Bezeichnung'}
        {punkt.formal ? <span style={{ marginLeft: 8, fontSize: T.tMikro, fontWeight: 600, color: T.muted }}>Korrektur</span> : null}
      </div>
      {e ? <span style={markeStil(e.schrift, e.rand, e.grund)}>{e.text}</span> : null}
    </div>
  );
}

/** Antwort an den Kunden und Gegenvorschlag - das, worum es beim Punkt geht. */
function Aussage({ punkt }) {
  // Bewertungen von vor dem 23.09.2026 haben keine Antwort; dann steht die
  // alte Einschaetzung da, damit der Punkt nicht leer wirkt.
  if (!punkt.antwort && !punkt.gegenvorschlag) {
    const alt = ersatzAussage(punkt);
    return alt ? <div style={{ fontSize: T.tText, color: T.body, lineHeight: 1.6 }}>{alt}</div> : null;
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {punkt.antwort ? (
        <div style={{ fontSize: T.tText, color: T.ink, lineHeight: 1.65 }}>{punkt.antwort}</div>
      ) : null}
      {punkt.gegenvorschlag ? (
        <div style={{
          background: T.grund, borderRadius: T.rChip, padding: '8px 11px',
          borderLeft: `3px solid ${T.orange}`, fontSize: T.tText, color: T.ink, lineHeight: 1.6,
        }}>
          <span style={{ fontWeight: 700, color: T.orangeTinte }}>Gegenvorschlag: </span>
          {punkt.gegenvorschlag}
        </div>
      ) : null}
    </div>
  );
}

function Einschaetzung({ titel, urteil }) {
  const f = urteilsFarbe(urteil?.bewertung);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: T.tMikro, color: T.body }}>
      <span style={{ width: 7, height: 7, borderRadius: 999, background: f.schrift, flexShrink: 0 }} />
      {titel} {urteil?.bewertung || 'unklar'}
    </span>
  );
}

/**
 * Die Vertragsstelle, aufgeklappt. Paragraph und "geprueft" stehen schon im
 * Schalter darueber - hier nur noch Ueberschrift und Wortlaut.
 */
function Zitat({ stelle }) {
  return (
    <div style={{
      marginTop: 10, padding: '9px 12px', borderRadius: T.rChip, background: T.grund,
      borderLeft: `3px solid ${stelle.geprueft ? T.navy : T.warn}`,
    }}>
      {stelle.ueberschrift ? (
        <div style={{ fontSize: T.tMikro, fontWeight: 700, color: T.muted, marginBottom: 4 }}>
          {[stelle.stelle, stelle.ueberschrift].filter(Boolean).join(' · ')}
        </div>
      ) : null}
      {stelle.wortlaut ? (
        <div style={{ fontSize: T.tMeta, lineHeight: 1.6, color: T.ink, fontStyle: 'italic' }}>„{stelle.wortlaut}“</div>
      ) : null}
      {!stelle.geprueft ? (
        <div style={{ fontSize: T.tMikro, color: T.warn, marginTop: 5 }}>
          {STELLE_NACHSEHEN}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Hintergrund zum Punkt, alles eingeklappt: Vertragsstelle, Begruendung,
 * Wissensbasis. Immer nur eines offen - sonst wird die Karte zur Wand.
 */
function Hintergrund({ punkt, mitVertrag, beleg, belegeLaden }) {
  const [offen, setOffen] = useState('');
  if (punkt.formal) return null;
  const umschalten = (was) => () => setOffen((v) => (v === was ? '' : was));
  const stelle = punkt.vertragsstelle;
  const trefferZahl = beleg?.treffer?.length || 0;

  return (
    <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${T.hairline}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', rowGap: 6 }}>
        <Einschaetzung titel="Wirtschaftlich" urteil={punkt.wirtschaftlich} />
        <Einschaetzung titel="Investoren" urteil={punkt.verkaeuflich} />
        <span style={{ flex: 1 }} />
        {stelle ? (
          <Aufklapper offen={offen === 'vertrag'} onClick={umschalten('vertrag')}
            farbe={stelle.geprueft ? T.muted : T.warn}
            titel={stelle.geprueft ? STELLE_GEPRUEFT : STELLE_UNGEPRUEFT}>
            {stelle.geprueft ? <Check size={11} /> : <AlertTriangle size={11} />}
            {stelle.stelle || 'Vertrag'}
          </Aufklapper>
        ) : mitVertrag ? (
          <span style={{ fontSize: T.tMikro, color: T.faint }}>im Vertrag nicht geregelt</span>
        ) : null}
        <Aufklapper offen={offen === 'begruendung'} onClick={umschalten('begruendung')}>Begründung</Aufklapper>
        <Aufklapper offen={offen === 'belege'} onClick={umschalten('belege')}>
          {belegeLaden && !beleg ? 'Wissensbasis …' : `Wissensbasis${trefferZahl ? ` ${trefferZahl}` : ''}`}
        </Aufklapper>
      </div>

      {offen === 'vertrag' && stelle ? <Zitat stelle={stelle} /> : null}
      {offen === 'begruendung' ? (
        <div style={{ display: 'grid', gap: 6, marginTop: 10 }}>
          {[['Wirtschaftlich', punkt.wirtschaftlich], ['Investoren', punkt.verkaeuflich]].map(([titel, urteil]) => (
            <div key={titel} style={{ fontSize: T.tMeta, color: T.body, lineHeight: 1.6 }}>
              <strong style={{ color: T.navy }}>{titel}:</strong> {urteil?.text || '—'}
            </div>
          ))}
        </div>
      ) : null}
      {offen === 'belege' && (beleg || belegeLaden) ? <Belegliste eintrag={beleg} laedt={belegeLaden} /> : null}
      {offen === 'belege' && !beleg && !belegeLaden ? (
        <div style={{ marginTop: 10, fontSize: T.tMikro, color: T.faint }}>Nichts Passendes gefunden.</div>
      ) : null}
    </div>
  );
}

/**
 * Ein offener Punkt: uebernehmen oder kommentieren.
 *
 * Der Kommentar wird beim Verlassen des Felds gespeichert, gesendet wird er
 * erst gesammelt mit den anderen ("N Kommentare an die KI").
 */
export function OffenerPunkt({
  punkt, mitVertrag, beleg, belegeLaden, gesperrt, entwurf, onEntwurf, onUebernehmen, onKommentar,
}) {
  // Nach jeder Runde der KI wird die Karte neu aufgebaut (Schluessel mit der
  // Runde) - ein erledigter Kommentar schliesst damit auch sein Feld.
  const [feldOffen, setFeldOffen] = useState(Boolean(punkt.kommentar));
  const text = entwurf ?? punkt.kommentar ?? '';
  const hatKommentar = Boolean(text.trim());
  return (
    <div style={{
      ...karteStil,
      padding: '14px 16px 12px',
      borderColor: hatKommentar ? T.orange : T.randWeich,
      opacity: gesperrt ? 0.6 : 1,
      transition: 'opacity 150ms, border-color 150ms',
    }}>
      <Kopfzeile punkt={punkt} />
      <Aussage punkt={punkt} />
      <Hintergrund punkt={punkt} mitVertrag={mitVertrag} beleg={beleg} belegeLaden={belegeLaden} />

      {feldOffen ? (
        <textarea
          value={text}
          autoFocus={!hatKommentar}
          onChange={(e) => onEntwurf(punkt.id, e.target.value)}
          onBlur={() => { if (text.trim() !== (punkt.kommentar || '')) onKommentar(punkt.id, text); }}
          rows={2}
          disabled={gesperrt}
          aria-label={`Kommentar zu Punkt ${punkt.nummer}`}
          placeholder={KOMMENTAR_PLATZHALTER}
          style={{ ...feldStil, marginTop: 12, resize: 'vertical', fontSize: T.tMeta }}
        />
      ) : null}

      <div style={{ display: 'flex', gap: 6, marginTop: 12, alignItems: 'center' }}>
        <button type="button" disabled={gesperrt} onClick={() => onUebernehmen(punkt.id)}
          style={{ ...knopfStil(true), cursor: gesperrt ? 'default' : 'pointer' }}>
          <Check size={12} /> Passt
        </button>
        {/* Offen und leer laesst sich das Feld wieder schliessen; mit Text
            bleibt es stehen, bis der Kommentar an die KI gegangen ist. */}
        {!feldOffen || !hatKommentar ? (
          <button type="button" disabled={gesperrt} onClick={() => setFeldOffen((v) => !v)}
            style={{ ...knopfStil(), cursor: gesperrt ? 'default' : 'pointer' }}>
            <MessageSquare size={12} /> {feldOffen ? 'Abbrechen' : 'Kommentieren'}
          </button>
        ) : null}
      </div>
    </div>
  );
}

/** Ein uebernommener Punkt - knapp, mit dem Weg zurueck. */
export function UebernommenerPunkt({ punkt, gesperrt, onZurueck }) {
  return (
    <div style={{
      ...karteStil, padding: '14px 16px 12px', borderLeft: `3px solid ${T.gut}`,
      opacity: gesperrt ? 0.6 : 1, transition: 'opacity 150ms',
    }}>
      <Kopfzeile punkt={punkt} />
      <Aussage punkt={punkt} />
      <div style={{ display: 'flex', marginTop: 10 }}>
        <button type="button" disabled={gesperrt} onClick={() => onZurueck(punkt.id)} style={{
          ...textKnopfStil, cursor: gesperrt ? 'default' : 'pointer',
        }}>
          <Undo2 size={12} /> Zurück zu offen
        </button>
      </div>
    </div>
  );
}
